// @flow

import { getLocale } from '../language.js';
import { useLocale } from './locale.js';
import { useUserData } from './user-data.js';

type Params = {|
  scale?: number,
  allowNegative?: boolean,
|};

export const useNumberFormat = ({
  scale = 0,
  allowNegative = false,
}: Params): ({|
  accept: RegExp,
  format: (string: string) => string,
  parse: (string: string) => string,
|}) => {
  const { countryDomain } = useUserData();
  const { numberLocale, language } = useLocale();

  let accept;
  let separator;
  if (['fr-FR', 'es-ES'].includes(getLocale(countryDomain, language))) {
    accept = allowNegative ? /[\d-.,]+/g : /[\d.,]+/g;
    separator = ',';
  } else {
    accept = allowNegative ? /[\d-.]+/g : /[\d.]+/g;
    separator = '.';
  }
  if (scale === 0) {
    accept = allowNegative ? /[\d-]+/g : /[\d]+/g;
  }

  const parse = (string: string) => string.match(accept)?.join('') ?? '';

  const format = (string: string) => {
    const parsed = parse(string);
    if (allowNegative && parsed === '-') {
      return parsed;
    }
    const [head, tail] = parsed.split(separator);
    // Avoid rounding errors at toLocaleString as when user enters 1.239 and maxDigits=2 we
    // must not to convert it to 1.24, it must stay 1.23
    const scaledTail = tail != null ? tail.slice(0, scale) : '';

    const number = Number.parseFloat(`${head}${separator}${scaledTail}`);

    if (Number.isNaN(number)) {
      return '';
    }

    const formatted = numberLocale.format(number, {
      minimumFractionDigits: 0,
      maximumFractionDigits: scale,
    });

    if (parsed.includes(separator)) {
      const [formattedHead] = formatted.split(separator);

      // skip zero at digits position for non fixed floats
      // as at digits 2 for non fixed floats numbers like 1.50 has no sense, just 1.5 allowed
      // but 1.0 has sense as otherwise you will not be able to enter 1.05 for example
      const formattedTail =
        scaledTail !== '' && scaledTail[scale - 1] === '0'
          ? scaledTail.slice(0, -1)
          : scaledTail;

      return `${formattedHead}${separator}${formattedTail}`;
    }
    return formatted;
  };

  return {
    accept,
    parse,
    format,
  };
};
